import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from './Modal';
import { apiFetch } from '../api';
import { Badge, CopyButton, EmptyState, Icon, cx } from './ui';
import {
  deepLinkProblem, leastLoadedBot, qrFileName, renderCouponQr, telegramDeepLink,
} from '../utils/couponQr';

// The Coupons page's QR dialog: pick one of the pool's Telegram bots and get a code that
// opens that bot and sends "/start <coupon>", which redeems the coupon. Drawn in the browser
// and never saved (utils/couponQr.js). The bot matters: a user who joins through a pool bot
// stays bound to it, so every scan of this code lands on the bot picked here, and the user
// counts are there to spread people out. The default is the bot with the fewest users.

function usersText(n) {
  if (!n) return 'No users yet';
  return n === 1 ? '1 user' : `${n.toLocaleString()} users`;
}

export default function CouponQrModal({ coupon, onClose }) {
  const [bots, setBots] = useState(null);           // null while loading
  const [telegramPolicy, setTelegramPolicy] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [botId, setBotId] = useState(null);
  const [image, setImage] = useState(null);         // { link, dataUrl }
  const [renderError, setRenderError] = useState(null);

  const problem = deepLinkProblem(coupon.code);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [b, p] = await Promise.all([
          apiFetch('/api/telegram-bots'), apiFetch('/api/channel-policy'),
        ]);
        if (!b.ok) throw new Error('The Telegram bots did not load.');
        const list = await b.json();
        // The policy only adds a warning, so a failure to read it is not an error here.
        const policy = p.ok ? await p.json().catch(() => null) : null;
        if (cancelled) return;
        setBots(Array.isArray(list) ? list : []);
        setBotId(leastLoadedBot(list)?.id ?? null);
        setTelegramPolicy(policy?.channels?.telegram ?? null);
      } catch (e) {
        if (!cancelled) setLoadError(e.message || 'The Telegram bots did not load.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const bot = (bots || []).find((b) => b.id === botId) || null;
  // Badged only when it really has fewer than every other bot: on a tie the badge would
  // single out one of several equal choices.
  const fewest = leastLoadedBot(bots);
  const fewestId = fewest && (bots || []).every((b) => b.id === fewest.id
    || (b.user_count ?? 0) > (fewest.user_count ?? 0)) ? fewest.id : null;
  const link = bot && !problem ? telegramDeepLink(bot.username, coupon.code) : null;

  useEffect(() => {
    if (!link) return undefined;
    let cancelled = false;
    setRenderError(null);
    renderCouponQr(link).then(
      (dataUrl) => { if (!cancelled) setImage({ link, dataUrl }); },
      (e) => { if (!cancelled) setRenderError(e?.message || 'The QR code could not be drawn.'); },
    );
    return () => { cancelled = true; };
  }, [link]);

  const ready = !!image && image.link === link;
  // A code that will be refused is still worth printing (the coupon can be fixed later),
  // so these warn rather than block.
  const warnings = [];
  if (coupon.is_expired) {
    warnings.push({ key: 'expired', text: 'This coupon has expired, so the bot will refuse the code until you extend it.' });
  } else if (coupon.is_exhausted) {
    warnings.push({ key: 'exhausted', text: 'This coupon has no uses left, so the bot will refuse the code until you raise its limit.' });
  }
  if (telegramPolicy && telegramPolicy.coupons_enabled === false) {
    warnings.push({ key: 'policy', text: 'Coupons are switched off on Telegram, so the bot will ignore the code.', channels: true });
  }

  let body;
  if (problem) {
    body = (
      <div className="notice notice-danger mb-0" role="alert">
        <Icon name="alertCircle" />
        <div className="notice-body">
          <div className="notice-title">This code cannot go in a Telegram link</div>
          {problem} Create a coupon with a shorter code to get a QR code for it.
        </div>
      </div>
    );
  } else if (loadError) {
    body = (
      <div className="notice notice-danger mb-0" role="alert">
        <Icon name="alertCircle" />
        <div className="notice-body">{loadError}</div>
      </div>
    );
  } else if (bots === null) {
    body = <div className="qr-loading" role="status"><span className="spinner" /> Loading the bots</div>;
  } else if (bots.length === 0) {
    body = (
      <EmptyState icon="bot" title="No Telegram bots yet"
                  action={<Link to="/channels" className="btn btn-secondary">Open Channels</Link>}>
        A QR code opens one of the pool&apos;s bots. Add a bot on the Channels page first.
      </EmptyState>
    );
  } else {
    body = (
      <div className="qr-layout">
        <fieldset className="qr-bots">
          <legend className="form-label">Telegram bot</legend>
          <div className="qr-bot-list">
            {bots.map((b) => (
              <label key={b.id} className={cx('check-row card-inset qr-bot', b.id === botId && 'is-selected')}>
                <input type="radio" name="qr-bot" value={b.id} checked={b.id === botId}
                       onChange={() => setBotId(b.id)} />
                <span className="grow min-w-0">
                  <span className="mono fw-500 truncate">@{b.username}</span>
                  <span className="check-hint">{usersText(b.user_count)}</span>
                </span>
                {bots.length > 1 && fewestId === b.id && <Badge tone="ok">Fewest users</Badge>}
              </label>
            ))}
          </div>
          <p className="form-hint">
            Everyone who joins with this code stays on the bot you pick, so spread them out.
          </p>
        </fieldset>

        <figure className="qr-preview m-0">
          <div className="qr-frame">
            {ready ? (
              <img src={image.dataUrl} alt={`QR code opening @${bot.username} with the coupon ${coupon.code}`}
                   width={240} height={240} />
            ) : renderError ? (
              <span className="text-danger text-sm" role="alert">{renderError}</span>
            ) : (
              <span className="spinner" role="status" aria-label="Drawing the QR code" />
            )}
          </div>
          <figcaption className="stack gap-1">
            <span className="form-label mb-0">Link in the code</span>
            {/* Shown whole, wrapped: it is exactly what a phone will open. */}
            <span className="copy-field qr-link">
              <span>{link}</span>
              <CopyButton value={link} label="Copy the link" />
            </span>
          </figcaption>
        </figure>
      </div>
    );
  }

  const canDownload = !problem && ready && !!bot;
  return (
    <Modal open onClose={onClose} title={`QR code for ${coupon.code}`} maxWidth={760}
           description="Scanning it opens the Telegram bot you pick with the code already sent, which redeems the coupon.">
      {warnings.map((w) => (
        <div key={w.key} className="notice notice-warn" role="status">
          <Icon name="alert" />
          <div className="notice-body">
            {w.text}
            {w.channels && <> The switch is on <Link to="/channels">Channels</Link>.</>}
          </div>
        </div>
      ))}
      {body}
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        {canDownload ? (
          <a className="btn btn-primary" href={image.dataUrl} download={qrFileName(coupon.code, bot.username)}>
            <Icon name="download" /> Download PNG
          </a>
        ) : (
          <button type="button" className="btn btn-primary" disabled>
            <Icon name="download" /> Download PNG
          </button>
        )}
      </div>
    </Modal>
  );
}
